/*! FFFit — generalized auto-fit engine.
 * Measures each text candidate's NATURAL text width with a Range and shrinks
 * font-size until the text fits its parent's available width (1 line) or a
 * given line count (2 lines via data-fit2). No hardcoded selector list, no
 * nowrap requirement on the source markup, no scrollWidth==clientWidth trap:
 * the Range gives the true rendered text box regardless of element width mode.
 *
 *   window.FFFit.fit(root, opts)   — one pass over root
 *   window.FFFit.observe(root)     — fit + keep fitting (resize/mutation/fonts)
 *
 * Safe no-op if root is null/missing. No external deps.
 */
(function (w, d) {
  'use strict';
  if (w.FFFit) return;

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, SVG: 1, IMG: 1, INPUT: 1, BR: 1, NOSCRIPT: 1, TEXTAREA: 1, SELECT: 1, OPTION: 1, PATH: 1, CANVAS: 1, VIDEO: 1 };

  function hasText(el) {
    // true if the element itself directly holds non-whitespace text
    for (var n = el.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3 && n.nodeValue && /\S/.test(n.nodeValue)) return true;
    }
    return false;
  }

  function isInline(el) {
    var disp = w.getComputedStyle(el).display;
    return disp === 'inline' || disp === 'contents';
  }

  // A candidate is a "leaf-ish" text holder: it carries meaningful text and has
  // NO descendant block-level element that itself carries text. Inline children
  // (a, span, b, s, strong, em...) are fine — they're part of this element's line.
  function hasBlockTextChild(el) {
    var kids = el.children;
    for (var i = 0; i < kids.length; i++) {
      var c = kids[i];
      if (SKIP_TAGS[c.tagName]) continue;
      var carries = hasText(c) || hasBlockTextChild(c);
      if (!carries) continue;
      // a child that carries text disqualifies THIS element as a leaf only if
      // that child is block-level (own line). Inline text children are ok.
      if (!isInline(c)) return true;
    }
    return false;
  }

  function meaningful(el) {
    // element's own + inline-descendant text, trimmed
    var t = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return t.length > 0;
  }

  function collect(root, out) {
    for (var n = root.firstElementChild; n; n = n.nextElementSibling) {
      if (SKIP_TAGS[n.tagName]) continue;
      if (n.hasAttribute && n.hasAttribute('data-no-fit')) continue;
      if (n.closest && n.closest('[data-no-fit]')) continue;
      var isCand = meaningful(n) && !hasBlockTextChild(n);
      if (isCand) {
        out.push(n);
      } else {
        collect(n, out); // descend to find leaf-ish holders deeper down
      }
    }
  }

  function pad(cs, a, b) {
    return (parseFloat(cs[a]) || 0) + (parseFloat(cs[b]) || 0);
  }

  // inner content-box width of an element (clientWidth minus horizontal padding)
  function innerW(el) {
    var cs = w.getComputedStyle(el);
    return el.clientWidth - pad(cs, 'paddingLeft', 'paddingRight');
  }

  // The hard upper bound for any text's available width: the content width of the
  // nearest [data-fit-root] (or #CartDrawer) ancestor. No intermediate auto-width
  // wrapper may make the engine think there's more room than the drawer actually
  // has. Cached per-pass via a Weakish lookup on the element.
  function rootCap(el) {
    var p = el.parentElement, guard = 0, cap = Infinity;
    while (p && guard++ < 40) {
      var iw = innerW(p);
      if (iw > 0) {
        if (p.hasAttribute && (p.hasAttribute('data-fit-root') || p.id === 'CartDrawer')) {
          return iw; // authoritative bound
        }
      }
      p = p.parentElement;
    }
    return cap;
  }

  // available content-box width for the text. If the element is auto-width
  // (shrink-to-fit), its own clientWidth just hugs the text, so walk up to the
  // nearest ancestor that is genuinely width-constrained and use its inner width.
  // The result is CLAMPED to the nearest width-bounded scroll/offset ancestor and
  // never exceeds the [data-fit-root]/#CartDrawer content width — so an unbounded
  // intermediate wrapper can never trick the engine into "everything fits".
  function availWidth(el) {
    var cap = rootCap(el); // hard ceiling from the fit-root / drawer
    var cs = w.getComputedStyle(el);
    var cw = el.clientWidth - pad(cs, 'paddingLeft', 'paddingRight');
    var auto = cs.width === 'auto' || isInline(el) ||
               cs.display === 'inline-block' || cs.display.indexOf('flex') > -1 && cs.flexBasis === 'auto' && cs.width === 'auto';
    var val = 0;
    if (!auto && cw > 0) {
      val = cw;
    } else {
      // climb to a constrained ancestor
      var p = el.parentElement, guard = 0;
      while (p && guard++ < 12) {
        var pcs = w.getComputedStyle(p);
        var pw = p.clientWidth - pad(pcs, 'paddingLeft', 'paddingRight');
        var pAuto = pcs.display === 'inline' || pcs.width === 'auto' && pcs.display === 'inline-block';
        if (pw > 0 && !pAuto) { val = pw; break; }
        p = p.parentElement;
      }
      if (!val) val = cw > 0 ? cw : (el.clientWidth || 0);
    }
    // Robustness clamp: if our measured "available" width meets/exceeds the
    // offsetParent or scroll container (a sign the element/wrapper is unbounded),
    // fall back to that bounded ancestor's inner width instead of trusting the
    // ballooned measurement.
    var op = el.offsetParent;
    if (op && op !== el) {
      var opw = innerW(op);
      if (opw > 0 && val >= opw - 0.5) val = Math.min(val, opw);
    }
    // Never exceed the fit-root / drawer content width.
    if (isFinite(cap) && cap > 0 && val > cap) val = cap;
    return val;
  }

  function textWidth(el) {
    try {
      var r = d.createRange();
      r.selectNodeContents(el);
      return r.getBoundingClientRect().width;
    } catch (e) { return el.scrollWidth; }
  }

  // Set font-size with !important priority. The engine's inline style MUST win
  // over stylesheet rules like `@media(max-width:768px){.X-v3-card h3{font-size:14px
  // !important}}` — a plain inline font-size loses to those, so the shrink had no
  // visible effect on the real phone (the original root cause of this bug).
  function setFS(el, px) {
    if (px == null) { el.style.removeProperty('font-size'); return; }
    el.style.setProperty('font-size', px + 'px', 'important');
  }
  function curFS(el) {
    return parseFloat(w.getComputedStyle(el).fontSize) || 0;
  }

  // Is this 1-line element currently rendering on more than one line? (wrapped)
  function isWrapped(el) {
    var cs = w.getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (isNaN(lh) || !lh) lh = (parseFloat(cs.fontSize) || 12) * 1.25;
    // allow a little slack so single-line elements aren't misjudged
    return el.scrollHeight > lh * 1.5;
  }

  function fitOne(el) {
    var maxLines = (el.hasAttribute('data-fit2') ? 2 : 1);

    // (a) reset to the element's PRISTINE base size, captured ONCE before the
    // engine ever touched it. removeProperty() here would WIPE an author's inline
    // font-size and fall back to the larger inherited size -- which silently
    // inflated every inline-sized element (root cause of "everything too big").
    var baseAttr = el.getAttribute('data-fff-base');
    var natural;
    if (baseAttr == null) { natural = curFS(el); if (natural) el.setAttribute('data-fff-base', String(natural)); }
    else { natural = parseFloat(baseAttr) || curFS(el); }
    var size = natural;
    if (!size) return;
    setFS(el, size); // pin to the base (author) size as the maximum

    // (b) let it shrink within its flex/grid parent and (1-line) stay on one line.
    // Use !important on nowrap too so a stylesheet white-space rule can't re-wrap
    // the line out from under us (mirrors the font-size !important fix).
    el.style.setProperty('min-width', '0', 'important');
    var prevWS = el.style.whiteSpace;
    if (maxLines === 1) el.style.setProperty('white-space', 'nowrap', 'important');

    var minAttr = parseFloat(el.getAttribute('data-fit-min'));
    var minPx = !isNaN(minAttr) ? minAttr : Math.max(9, size * 0.6);

    var guard = 0;
    if (maxLines === 1) {
      // (c)+(d) measure TEXT width via Range vs available content width.
      // availWidth() is clamped to the fit-root / drawer so an unbounded wrapper
      // can't report a too-large available width.
      var avail = availWidth(el);
      if (avail > 0) {
        var tw = textWidth(el);
        // Shrink while the text is wider than available OR it is still wrapping
        // to a second line (rendered height > one line) at the current size.
        while ((tw > avail - 2 || isWrapped(el)) && size > minPx && guard++ < 200) {
          size -= 0.5;
          setFS(el, size);
          tw = textWidth(el);
        }
      }
    } else {
      // 2-line: allow wrapping, shrink until rendered height fits 2 lines
      el.style.whiteSpace = prevWS || '';
      var cs2 = w.getComputedStyle(el);
      var lh = parseFloat(cs2.lineHeight);
      if (isNaN(lh)) lh = size * 1.25;
      var maxH = lh * maxLines + 1;
      while (el.scrollHeight > maxH && size > minPx && guard++ < 200) {
        size -= 0.5;
        setFS(el, size);
        cs2 = w.getComputedStyle(el);
        lh = parseFloat(cs2.lineHeight) || size * 1.25;
        maxH = lh * maxLines + 1;
      }
    }
  }

  function fit(root, opts) {
    if (typeof root === 'string') root = d.querySelector(root);
    if (!root) return; // safe no-op
    var list = [];
    collect(root, list);
    for (var i = 0; i < list.length; i++) {
      try { fitOne(list[i]); } catch (e) {}
    }
  }

  // batched/debounced runner
  function makeRunner(root) {
    var raf = 0, pending = false;
    return function run() {
      if (pending) return;
      pending = true;
      var go = function () { pending = false; fit(root); };
      if (w.requestAnimationFrame) raf = w.requestAnimationFrame(go);
      else go();
    };
  }

  function debounce(fn, ms) {
    var t = 0;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  function observe(root, opts) {
    if (typeof root === 'string') root = d.querySelector(root);
    if (!root) return function () {}; // safe no-op disconnect
    var run = makeRunner(root);
    var draw = debounce(run, 80);

    run(); // initial pass

    var ro = null, mo = null;
    if (w.ResizeObserver) { ro = new w.ResizeObserver(draw); try { ro.observe(root); } catch (e) {} }
    if (w.MutationObserver) {
      mo = new w.MutationObserver(draw);
      try { mo.observe(root, { childList: true, subtree: true, characterData: true }); } catch (e) {}
    }
    var onResize = debounce(run, 120);
    w.addEventListener('resize', onResize);

    if (d.fonts && d.fonts.ready) { try { d.fonts.ready.then(run); } catch (e) {} }
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', run);
    w.addEventListener('load', run);

    return function disconnect() {
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      w.removeEventListener('resize', onResize);
    };
  }

  w.FFFit = { fit: fit, observe: observe };
})(window, document);
